import React, { createContext, useCallback, useContext, useState } from 'react';
import { DtoContentType } from '@/shared/constants';
import type { UndoableState } from '../state/types';
import type { SnapPoint } from './utils/snap-points';

export type AssetDropTrackTargetType =
  | 'existing'
  | 'create-at-top'
  | 'create-at-bottom'
  | 'create-between';

export interface AssetDropPreviewState {
  /** Projection is for timeline presentation only. It is invalidated by any real document change. */
  baseDocument?: UndoableState;
  baseLinkingEnabled?: boolean;
  baseSnappingEnabled?: boolean;
  projectedDocument?: UndoableState;
  inserted?: boolean;
  estimatedDuration?: boolean;
  snapPoint?: SnapPoint | null;
  /** 드롭될 프레임 위치 */
  frame: number;
  /** 아이템의 duration (프레임) */
  durationInFrames: number;
  /** 에셋 타입 */
  contentType: DtoContentType;
  /** 에셋 이름 */
  name: string;
  /** 드롭될 트랙 인덱스 (null이면 새 트랙 생성) */
  trackIndex: number | null;
  /** 트랙 타겟 타입 (기존 트랙 / 상단 새 트랙 / 하단 새 트랙 / 중간 삽입) */
  trackTargetType: AssetDropTrackTargetType;
  /** 해당 위치에 배치 가능 여부 */
  isAvailable: boolean;
  /** create-between일 때 삽입될 트랙 인덱스 */
  insertAtIndex?: number;
}

interface AssetDropPreviewContextValue {
  previewState: AssetDropPreviewState | null;
  setPreviewState: (state: AssetDropPreviewState | null) => void;
}

const AssetDropPreviewContext =
  createContext<AssetDropPreviewContextValue | null>(null);

export const AssetDropPreviewProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [previewState, setPreviewState] =
    useState<AssetDropPreviewState | null>(null);

  const value = {
    previewState,
    setPreviewState: useCallback(
      (state: AssetDropPreviewState | null) => setPreviewState(state),
      [],
    ),
  };

  return (
    <AssetDropPreviewContext.Provider value={value}>
      {children}
    </AssetDropPreviewContext.Provider>
  );
};

export const useAssetDropPreview = () => {
  const ctx = useContext(AssetDropPreviewContext);
  if (!ctx) {
    throw new Error(
      'useAssetDropPreview must be used within AssetDropPreviewProvider',
    );
  }
  return ctx;
};
